import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Coins } from 'lucide-react';

import { db } from '../utils/firebaseApp.js';
import { AuthContext } from '../utils/auth.js';
import cn from '../ui/cn.js';
import Skeleton from '../ui/Skeleton.js';

/** Live page balance for the current user (credits / 10 = pages). */
export function useCredits() {
  const { currentUser } = useContext(AuthContext);
  const userId = currentUser?.uid;
  const [pages, setPages] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    setPages(null);
    const creditsRef = ref(db, `userDetails/${userId}/credits/credits`);
    const unsubscribe = onValue(
      creditsRef,
      (snapshot) => {
        const data = snapshot.val();
        setPages(data !== null && data !== undefined ? Number(data) / 10 : 0);
      },
      () => setPages(0),
    );
    return () => unsubscribe();
  }, [userId]);

  return pages;
}

/** Top bar pill showing the remaining pages. Links to Billing. */
export default function CreditsPill({ className }) {
  const pages = useCredits();
  const low = pages !== null && pages < 10;

  return (
    <Link
      to="/billing"
      title="Buy pages"
      className={cn(
        'group inline-flex h-8 items-center gap-1.5 rounded-full pl-2.5 pr-1 text-xs font-medium ring-1 ring-inset transition-colors',
        low ? 'bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100' : 'bg-indigo-50 text-indigo-700 ring-indigo-100 hover:bg-indigo-100',
        className,
      )}
      aria-live="polite"
    >
      <Coins className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden="true" />
      {pages === null ? (
        <Skeleton className="h-3 w-14 bg-indigo-100" />
      ) : (
        <span className="tabular-nums">
          {pages.toLocaleString()} <span className="hidden sm:inline">pages</span>
        </span>
      )}
      <span className={cn('ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-inset transition group-hover:scale-105', low ? 'text-amber-700 ring-amber-200' : 'text-indigo-600 ring-indigo-100')}>
        <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </Link>
  );
}
